import React, { useState, useMemo, useCallback } from 'react';
import { toast } from 'react-toastify';
import OnboardingService from '../../../services/onboarding.service';

// Custom hook for sorting and filtering
export const useSortAndFilter = (data, sortColumn, sortOrder, setSortColumn, setSortOrder) => {
    const handleSortingColumn = useCallback(
        (event, column) => {
            event.preventDefault();
            const newSortOrder = sortColumn === column && sortOrder === 'ASCENDING' ? 'DESCENDING' : 'ASCENDING';
            setSortColumn(column);
            setSortOrder(newSortOrder);
        },
        [sortColumn, sortOrder, setSortColumn, setSortOrder]
    );

    const processedData = useMemo(() => {
        let sortedData = [...data];

        if (sortColumn) {
            sortedData.sort((a, b) => {
                let aVal = a[sortColumn];
                let bVal = b[sortColumn];

                if (typeof aVal === 'string') {
                    aVal = aVal.toLowerCase();
                    bVal = bVal.toLowerCase();
                    return sortOrder === 'ASCENDING' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
                }

                return sortOrder === 'ASCENDING' ? aVal - bVal : bVal - aVal;
            });
        }

        return sortedData;
    }, [data, sortColumn, sortOrder]);

    return {
        processedData,
        handleSortingColumn,
        sortColumn,
        sortOrder,
    };
};

// Custom hook for pagination
export const usePagination = (data, initialPageSize = 30) => {
    const [numberOfPage, setNumberOfPage] = useState(initialPageSize);
    const [currentPage, setCurrentPage] = useState(1);

    const totalPages = useMemo(() => Math.ceil(data.length / numberOfPage), [data.length, numberOfPage]);
    const startIdx = useMemo(() => (currentPage - 1) * numberOfPage, [currentPage, numberOfPage]);
    const endIdx = useMemo(() => startIdx + numberOfPage, [startIdx, numberOfPage]);
    const paginatedData = useMemo(() => data.slice(startIdx, endIdx), [data, startIdx, endIdx]);

    return {
        numberOfPage,
        setNumberOfPage,
        currentPage,
        setCurrentPage,
        totalPages,
        startIdx,
        paginatedData,
    };
};

// Custom hook for affiliate list management (data fetching + pagination)
export const useAffiliateList = () => {
    const [affiliateData, setAffiliateData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [totalRecords, setTotalRecords] = useState(0);
    const [currentPageIndex, setCurrentPageIndex] = useState(1);
    const [pageSize, setPageSize] = useState(30);
    const [refreshKey, setRefreshKey] = useState(0);

    React.useEffect(() => {
        setLoading(true);
        OnboardingService.getInvitations(currentPageIndex, pageSize)
            .then(response => {
                if (response?.data?.Status === 200 && response?.data?.EntityList) {
                    setAffiliateData(response.data.EntityList);
                    setTotalRecords(response.data.TotalRecords || 0);
                } else {
                    toast.error('Failed to fetch invitations');
                    setAffiliateData([]);
                }
            })
            .catch(error => {
                console.error('Error fetching invitations:', error);
                toast.error('Error fetching invitations');
                setAffiliateData([]);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [currentPageIndex, pageSize, refreshKey]);

    return {
        affiliateData,
        loading,
        totalRecords,
        currentPageIndex,
        setCurrentPageIndex,
        pageSize,
        setPageSize,
        refreshKey,
        setRefreshKey,
    };
};

// Custom hook for affiliate actions (CRUD operations)
export const useAffiliateActions = ({ setShowAddAffiliatePopup, setCurrentPageIndex, setRefreshKey }) => {
    const handleAddAffiliate = useCallback(formData => {
        return OnboardingService.createInvitation(formData.email)
            .then(response => {
                if (response?.data?.Status === 200) {
                    const successMessage = formData.email
                        ? 'Affiliate invitation sent successfully'
                        : 'Affiliate invitation created';
                    toast.success(successMessage);
                    setShowAddAffiliatePopup(false);
                    // Refresh invitations list
                    setCurrentPageIndex(1);
                    setRefreshKey(prev => prev + 1);
                } else {
                    toast.error(response?.data?.errorDescription || 'Failed to send invitation');
                    throw new Error(response?.data?.errorDescription || 'Failed to send invitation');
                }
            })
            .catch(error => {
                const errorMessage = error?.response?.data?.errorDescription || 'Error sending invitation';
                toast.error(errorMessage);
                throw error;
            });
    }, [setShowAddAffiliatePopup, setCurrentPageIndex, setRefreshKey]);

    const handleDeleteInvitation = useCallback(inviteId => {
        OnboardingService.deleteInvitation(inviteId)
            .then(response => {
                if (response?.data?.Status === 200) {
                    toast.success('Invitation deleted successfully');
                    // Refresh current page
                    setRefreshKey(prev => prev + 1);
                } else {
                    toast.error(response?.data?.errorDescription || 'Failed to delete invitation');
                }
            })
            .catch(error => {
                const errorMessage = error?.response?.data?.errorDescription || 'Error deleting invitation';
                toast.error(errorMessage);
            });
    }, [setRefreshKey]);

    const handleCopyInvitationUrl = useCallback(async (e, item) => {
        e.preventDefault();
        try {
            if (item.InvitationUrl) {
                await navigator.clipboard.writeText(item.InvitationUrl);
                toast.success('Invitation URL Copied');
            } else {
                toast.error('No URL available to copy');
            }
        } catch {
            toast.error('Failed to copy URL');
        }
    }, []);

    return {
        handleAddAffiliate,
        handleDeleteInvitation,
        handleCopyInvitationUrl,
    };
};
